export const CLAIMS_TERMS = [
    // ------------------------------------------------------------------ claims
    {
        id: 'fnol',
        term: 'First notice of loss',
        short: 'FNOL',
        aka: ['first report of injury', 'loss notice', 'claim report'],
        category: 'claims',
        essential: true,
        def: 'The initial report telling the carrier a loss has happened, which starts the claim.',
        detail: 'Report early and report on partial information. Late notice is the most common way an otherwise covered claim gets fought, and no policy requires the insured to know the answer before telling the carrier there is a question.',
        see: ['notice', 'late-notice', 'adjuster', 'claim'],
    },
    {
        id: 'notice',
        term: 'Notice',
        category: 'claims',
        essential: true,
        def: 'The policy condition requiring the insured to tell the carrier about a loss, a claim, or a circumstance that might become one.',
        detail: 'Occurrence policies usually say "as soon as practicable". Claims-made policies impose hard deadlines, and reporting a *circumstance* before the term ends is what locks the claim into the current policy year.',
        see: ['late-notice', 'fnol', 'claims-made-and-reported'],
    },
    {
        id: 'late-notice',
        term: 'Late notice',
        category: 'claims',
        def: 'Reporting a claim after the policy required. Whether it costs coverage usually turns on whether the delay actually prejudiced the carrier.',
        detail: 'The prejudice rule is a state-law question and it does not apply to claims-made policies, where the reporting deadline is a condition of coverage rather than a duty. On those, late is simply late.',
        see: ['notice', 'claims-made', 'prejudice'],
    },
    {
        id: 'prejudice',
        term: 'Prejudice',
        category: 'claims',
        def: 'Actual harm to the carrier\'s ability to investigate or defend, caused by the insured\'s breach of a condition. Most states require the carrier to show it before denying for late notice.',
        see: ['late-notice', 'notice'],
    },
    {
        id: 'adjuster',
        term: 'Adjuster',
        category: 'claims',
        essential: true,
        def: 'The person who investigates a claim, decides what the policy covers, and negotiates the payment.',
        detail: 'A staff adjuster works for the carrier. An independent adjuster is hired by the carrier. A public adjuster is hired by the *insured* and paid a percentage of the recovery.',
        see: ['public-adjuster', 'tpa', 'reserve'],
    },
    {
        id: 'public-adjuster',
        term: 'Public adjuster',
        category: 'claims',
        def: 'An adjuster the insured hires to present and negotiate their own claim, usually paid a percentage of the settlement.',
        detail: 'Common on large property losses. Their involvement changes the tone of a claim and their fee comes out of the insured\'s recovery, so it is worth the insured understanding both before signing.',
        see: ['adjuster'],
    },
    {
        id: 'tpa',
        term: 'Third-party administrator',
        short: 'TPA',
        category: 'claims',
        def: 'An outside firm that handles claims on behalf of a carrier, a captive, or a self-insured employer.',
        see: ['adjuster', 'captive', 'self-insurance'],
    },
    {
        id: 'reserve',
        term: 'Reserve',
        category: 'claims',
        essential: true,
        def: 'The carrier\'s estimate of what a claim will ultimately cost, set aside while it is open.',
        detail: 'Reserves count against the account\'s loss history exactly like paid dollars do, which is why an open claim with a conservative reserve can wreck a renewal. Reviewing and challenging stale reserves before marketing an account is real, chargeable work.',
        see: ['loss-run', 'incurred', 'ibnr', 'loss-ratio'],
    },
    {
        id: 'incurred',
        term: 'Incurred losses',
        category: 'claims',
        essential: true,
        def: 'Paid amounts plus outstanding reserves — the total a claim is currently expected to cost.',
        detail: 'It is the column that matters on a loss run. A file showing $5,000 paid and $95,000 reserved is a $100,000 claim as far as any underwriter is concerned.',
        see: ['reserve', 'loss-run', 'ibnr'],
    },
    {
        id: 'ibnr',
        term: 'Incurred but not reported',
        short: 'IBNR',
        category: 'claims',
        def: 'An actuarial allowance for losses that have happened but have not yet been reported.',
        detail: 'It is why a recent policy year always looks better than it will turn out to be, and why underwriters discount the most recent year on a loss run.',
        see: ['reserve', 'loss-development', 'incurred'],
    },
    {
        id: 'loss-development',
        term: 'Loss development',
        category: 'claims',
        def: 'The way losses grow after the year ends as claims mature and reserves are adjusted.',
        detail: 'Comp and liability develop upward for years; property is mostly settled quickly. Comparing a fresh year against a five-year-old year without allowing for development flatters the recent one.',
        see: ['ibnr', 'reserve', 'loss-run'],
    },
    {
        id: 'loss-run',
        term: 'Loss run',
        aka: ['loss history', 'claims history', 'loss experience'],
        category: 'documents',
        essential: true,
        def: 'A carrier report listing an account\'s claims over a period — dates, descriptions, amounts paid, reserves, and status.',
        detail: 'Underwriters want three to five years, currently valued (usually within 90 days), from each carrier. "No known losses" is not a loss run, and a submission without them mostly does not get quoted.',
        see: ['incurred', 'no-loss-letter', 'submission', 'valuation-date'],
    },
    {
        id: 'valuation-date',
        term: 'Valuation date',
        aka: ['valued as of'],
        category: 'documents',
        def: 'The date a loss run\'s figures were pulled. Reserves change, so a loss run is only accurate as of that date.',
        detail: 'Most underwriters will not accept anything more than 90 days old at binding, which is why loss runs requested early often have to be requested again.',
        see: ['loss-run', 'reserve'],
    },
    {
        id: 'no-loss-letter',
        term: 'No-loss letter',
        aka: ['statement of no loss', 'no known loss letter'],
        category: 'documents',
        def: 'A signed statement from the insured that no losses have occurred up to a stated date and time.',
        detail: 'Required for reinstatements, backdated coverage, and some late binds. It transfers the risk of a hidden loss to the insured, so it must be signed by someone who would actually know.',
        see: ['reinstatement', 'loss-run'],
    },
    {
        id: 'reservation-of-rights',
        term: 'Reservation of rights',
        short: 'ROR',
        category: 'claims',
        def: 'A letter saying the carrier will defend the claim but may later deny coverage for stated reasons.',
        detail: 'It preserves the carrier\'s position while the facts develop. In some states a serious conflict created by an ROR entitles the insured to independent counsel at the carrier\'s expense.',
        see: ['duty-to-defend', 'declaratory-judgment', 'coverage-denial'],
    },
    {
        id: 'coverage-denial',
        term: 'Denial',
        category: 'claims',
        def: 'The carrier\'s decision that the policy does not respond, with the reasons stated in writing.',
        detail: 'A denial is not the end of it. Read it against the actual form rather than the summary, check whether the cited exclusion has a carveback, and ask for the claim file and the policy as issued.',
        see: ['reservation-of-rights', 'bad-faith', 'declaratory-judgment'],
    },
    {
        id: 'declaratory-judgment',
        term: 'Declaratory judgment',
        aka: ['dj action'],
        category: 'claims',
        def: 'A lawsuit asking a court to decide whether the policy covers a claim, brought by either the carrier or the insured.',
        see: ['coverage-denial', 'reservation-of-rights'],
    },
    {
        id: 'bad-faith',
        term: 'Bad faith',
        category: 'claims',
        def: 'A carrier unreasonably denying, delaying or underpaying a claim. Where established, damages can exceed the policy limit.',
        detail: 'Failure to settle within limits when it could have is the classic first-party version. State law varies enormously on what qualifies and what it is worth.',
        see: ['coverage-denial', 'duty-to-defend', 'consent-to-settle'],
    },
    {
        id: 'proof-of-loss',
        term: 'Proof of loss',
        category: 'documents',
        def: 'A sworn statement from the insured detailing the loss and the amount claimed, usually required within a set period on property claims.',
        detail: 'The deadline is a policy condition — commonly 60 days from the carrier\'s request — and missing it is a defence carriers do use. Ask for an extension in writing rather than letting it pass.',
        see: ['duties-after-loss', 'appraisal-clause'],
    },
    {
        id: 'duties-after-loss',
        term: 'Duties after a loss',
        category: 'policy',
        def: 'What the insured must do once a loss occurs: notify the carrier, protect the property from further damage, keep records, cooperate, submit a proof of loss, and not admit liability.',
        see: ['notice', 'proof-of-loss', 'conditions'],
    },
    {
        id: 'appraisal-clause',
        term: 'Appraisal',
        category: 'claims',
        def: 'A property policy mechanism for resolving disagreement about the *amount* of a loss — each side names an appraiser, and the two pick an umpire.',
        detail: 'It settles value, not coverage. A carrier that says nothing is covered cannot be dragged into appraisal over the number.',
        see: ['proof-of-loss', 'arbitration'],
    },
    {
        id: 'arbitration',
        term: 'Arbitration',
        category: 'claims',
        def: 'Resolving a dispute before a private arbitrator instead of a court, often required by the policy or the underlying contract.',
        see: ['appraisal-clause', 'mediation'],
    },
    {
        id: 'mediation',
        term: 'Mediation',
        category: 'claims',
        def: 'A non-binding negotiation run by a neutral third party. Most litigated claims go through it before trial.',
        see: ['arbitration', 'settlement'],
    },
    {
        id: 'settlement',
        term: 'Settlement',
        category: 'claims',
        def: 'An agreed payment resolving a claim, normally in exchange for a signed release.',
        see: ['release', 'consent-to-settle', 'mediation'],
    },
    {
        id: 'release',
        term: 'Release',
        category: 'claims',
        def: 'The claimant\'s written agreement to give up further claims in exchange for the settlement payment.',
        see: ['settlement', 'subrogation'],
    },
    {
        id: 'salvage',
        term: 'Salvage',
        category: 'claims',
        def: 'Damaged property the carrier takes ownership of after paying a claim, and whatever it recovers by selling it.',
        see: ['subrogation', 'acv'],
    },
    {
        id: 'total-loss',
        term: 'Total loss',
        category: 'claims',
        def: 'Property damaged beyond economic repair. A constructive total loss is one where repair would cost more than the property is worth.',
        see: ['acv', 'replacement-cost', 'salvage'],
    },
    {
        id: 'acv',
        term: 'Actual cash value',
        short: 'ACV',
        category: 'property',
        essential: true,
        def: 'Replacement cost minus depreciation — what the property was worth in its used condition at the time of loss.',
        detail: 'On a fifteen-year-old roof, ACV can be a fraction of the cost to replace it. Many coastal and older-building policies quietly write roofs at ACV while everything else is replacement cost.',
        confused: 'Replacement cost pays to replace with new. ACV subtracts wear and age. Functional replacement cost pays to replace with modern equivalent materials.',
        see: ['replacement-cost', 'indemnity', 'total-loss'],
    },
    {
        id: 'replacement-cost',
        term: 'Replacement cost',
        short: 'RC',
        aka: ['rcv', 'replacement cost value'],
        category: 'property',
        essential: true,
        def: 'Pays what it costs to replace the damaged property with new property of like kind and quality, without deducting for depreciation.',
        detail: 'Almost always paid in two stages: ACV first, and the depreciation withheld until the insured actually replaces the property. An insured who cannot fund the gap never collects the full amount.',
        see: ['acv', 'coinsurance', 'ordinance-or-law'],
    },
    {
        id: 'subrogation-waiver-claim',
        term: 'Deductible recovery',
        category: 'claims',
        def: 'Getting the insured\'s deductible back out of a subrogation recovery, usually shared in proportion with the carrier.',
        see: ['subrogation', 'deductible'],
    },
    {
        id: 'self-insurance',
        term: 'Self-insurance',
        category: 'market',
        def: 'Deliberately retaining risk and paying losses from the business\'s own funds rather than transferring them.',
        detail: 'Formal self-insurance for workers\' comp requires state approval, security and a TPA. Informal self-insurance is what a very high deductible amounts to.',
        see: ['sir', 'captive', 'tpa'],
    },
    // --------------------------------------------------------------- documents
    {
        id: 'coi',
        term: 'Certificate of insurance',
        short: 'COI',
        aka: ['acord 25', 'cert', 'certificate'],
        category: 'documents',
        essential: true,
        def: 'A one-page summary showing that policies exist, with their limits and dates. It is evidence, not coverage, and it grants no rights.',
        detail: 'Never alter the ACORD wording, never promise notice of cancellation the policy does not provide, and never state additional insured or waiver status that is not actually endorsed on. A certificate that contradicts the policy is an agent\'s liability, not the carrier\'s.',
        confused: 'The certificate holder is not an additional insured. Listing them in the holder box adds nothing to their protection.',
        see: ['certificate-holder', 'additional-insured', 'acord', 'evidence-of-property'],
    },
    {
        id: 'acord',
        term: 'ACORD form',
        category: 'documents',
        essential: true,
        def: 'The standardised industry forms — applications, certificates, notices — that every carrier accepts. Numbered: 25 is the liability certificate, 125 the commercial application, 126 general liability, 130 workers\' comp, 140 property.',
        detail: 'They are standard so a submission can go to eight carriers without being retyped eight times. They are also copyrighted, which is why systems that generate them need a licence.',
        see: ['coi', 'application', 'evidence-of-property'],
    },
    {
        id: 'evidence-of-property',
        term: 'Evidence of property insurance',
        aka: ['acord 27', 'acord 28', 'epi'],
        category: 'documents',
        def: 'The certificate equivalent for property coverage, usually issued to a lender showing the mortgagee or loss payee.',
        see: ['coi', 'mortgagee', 'loss-payee'],
    },
    {
        id: 'application',
        term: 'Application',
        category: 'documents',
        essential: true,
        def: 'The form the insured completes describing their business, exposures and history. The carrier underwrites and prices from it, and it is normally attached to the policy.',
        detail: 'It must be signed by the insured, not by the agent on their behalf. Because it becomes part of the contract, an answer that turns out to be wrong is the insured\'s problem — unless the agent filled it in.',
        see: ['acord', 'material-misrepresentation', 'warranty-statement', 'supplemental-application'],
    },
    {
        id: 'supplemental-application',
        term: 'Supplemental application',
        aka: ['supplemental', 'supp'],
        category: 'documents',
        def: 'An extra questionnaire for a specific class or coverage — contractors, restaurants, ransomware controls, drivers.',
        detail: 'Underwriters use them to ask the questions the ACORD does not. They are also where warranties hide: a "yes" about MFA or a subcontractor certificate policy can be a condition of coverage rather than a rating question.',
        see: ['application', 'warranty-statement'],
    },
    {
        id: 'warranty-statement',
        term: 'Warranty statement',
        category: 'documents',
        def: 'A statement on an application that the insured warrants to be true, breach of which can void coverage rather than merely change the price.',
        detail: 'Common on cyber, professional liability and D&O — "no knowledge of any circumstance likely to give rise to a claim". Signed close to inception and treated as a formality, it is one of the sharper traps on a renewal.',
        see: ['application', 'material-misrepresentation', 'prior-knowledge'],
    },
    {
        id: 'prior-knowledge',
        term: 'Prior knowledge exclusion',
        category: 'professional',
        def: 'Excludes claims arising from something the insured already knew about before the policy started.',
        detail: 'It is why a circumstance should be reported to the *expiring* carrier before the term ends. Carrying it into a new policy year converts a covered circumstance into an excluded one.',
        see: ['warranty-statement', 'claims-made', 'notice'],
    },
    {
        id: 'bor',
        term: 'Broker of record letter',
        short: 'BOR',
        aka: ['agent of record letter', 'aor'],
        category: 'documents',
        essential: true,
        def: 'A letter signed by the insured naming a new agent to represent them on their policies. It transfers the account and the commission without changing the coverage.',
        detail: 'Carriers typically honour it after a short waiting period — often 5 to 10 days — during which the incumbent may respond. A BOR moves an existing policy; it does not create a new one, and it is the reason marketing an account that has just been BOR\'d rarely works.',
        confused: 'A BOR changes who services the policy. A remarket changes which carrier writes it.',
        see: ['agent-of-record', 'remarket', 'x-date'],
    },
    {
        id: 'agent-of-record',
        term: 'Agent of record',
        short: 'AOR',
        category: 'practice',
        def: 'The agency officially representing the insured on a policy — the one the carrier deals with and pays commission to.',
        see: ['bor', 'retail-agent'],
    },
    {
        id: 'notice-of-cancellation',
        term: 'Notice of cancellation',
        category: 'documents',
        def: 'The written notice a carrier must give before cancelling — typically 10 days for non-payment and 30 to 60 days otherwise, set by state law.',
        detail: 'It runs to the first named insured and to mortgagees and loss payees named on the policy. It does not run to certificate holders, whatever the certificate says.',
        see: ['cancellation', 'first-named-insured', 'certificate-holder'],
    },
    {
        id: 'tria-rejection',
        term: 'Terrorism rejection form',
        category: 'documents',
        def: 'The signed form recording that the insured was offered terrorism coverage and declined it.',
        detail: 'Federal law requires the offer be made and priced separately. Keep the signed rejection: without it, some carriers deem the coverage accepted and bill for it.',
        see: ['terrorism-coverage', 'declination-letter'],
    },
    {
        id: 'statement-of-values',
        term: 'Statement of values',
        short: 'SOV',
        category: 'documents',
        def: 'A schedule listing every insured location with its building value, contents value, business income, square footage, construction, year built and protection.',
        detail: 'It drives property pricing and it is what a margin clause measures against. An out-of-date SOV is the most common reason a large property claim underpays.',
        see: ['tiv', 'margin-clause', 'agreed-value', 'cope'],
    },
    // ---------------------------------------------------------------- practice
    {
        id: 'marketing-an-account',
        term: 'Marketing an account',
        category: 'practice',
        essential: true,
        def: 'Taking a risk to several carriers to get competing quotes.',
        detail: 'Blanket-shopping an account annoys markets and gets it blocked: two agents submitting the same risk to the same carrier ends in a market clearance fight and often a decline for both. Pick markets deliberately and clear them.',
        see: ['submission', 'market-clearance', 'remarket', 'appetite'],
    },
    {
        id: 'market-clearance',
        term: 'Market clearance',
        aka: ['blocking a market'],
        category: 'practice',
        def: 'Registering with a wholesaler or carrier that you are working a particular account, so a second agent cannot submit the same risk.',
        detail: 'First to clear generally holds it. It is why a wholesaler asks for the insured\'s name and address before anything else.',
        see: ['marketing-an-account', 'wholesale-broker'],
    },
    {
        id: 'remarket',
        term: 'Remarket',
        category: 'practice',
        def: 'Taking an existing account back out to other carriers, usually at renewal because of a rate increase or a coverage problem.',
        confused: 'A remarket changes the carrier. A BOR changes the agent.',
        see: ['marketing-an-account', 'renewal', 'bor'],
    },
    {
        id: 'proposal',
        term: 'Proposal',
        category: 'practice',
        def: 'The document presenting quoted options to the insured — coverages, limits, premiums, and what is not included.',
        detail: 'Include what was quoted and declined, and the taxes and fees. A proposal that shows only the recommended option and only the premium is the document an E&O plaintiff wishes you had written.',
        see: ['quote', 'declination-letter', 'taxes-and-fees'],
    },
    {
        id: 'gap-analysis',
        term: 'Coverage gap analysis',
        category: 'practice',
        def: 'Comparing what a business actually does against what its policies cover, to find exposures nobody has bought coverage for.',
        see: ['exposure-analysis', 'stewardship-report', 'proposal'],
    },
    {
        id: 'exposure-analysis',
        term: 'Exposure analysis',
        category: 'practice',
        def: 'Working out what could go wrong in a business and how badly — before deciding what to insure.',
        see: ['gap-analysis', 'exposure'],
    },
    {
        id: 'stewardship-report',
        term: 'Stewardship report',
        category: 'practice',
        def: 'An annual summary given to a client of what the agency did for them — claims handled, savings achieved, coverage added, services delivered.',
        detail: 'The practical defence against a competitor\'s price quote: it makes visible the work the client otherwise never sees.',
        see: ['gap-analysis', 'account-rounding'],
    },
    {
        id: 'account-rounding',
        term: 'Account rounding',
        category: 'practice',
        def: 'Writing more lines for an existing client — adding the umbrella, the auto, the work comp, the owner\'s home.',
        detail: 'Retention rises sharply with each additional policy, so rounding is a retention strategy as much as a sales one.',
        see: ['common-expiration-date', 'stewardship-report', 'cross-sell'],
    },
    {
        id: 'cross-sell',
        term: 'Cross-sell',
        category: 'practice',
        def: 'Selling a different kind of coverage to an existing client.',
        see: ['account-rounding'],
    },
    {
        id: 'prospecting',
        term: 'Prospecting',
        category: 'practice',
        def: 'Finding and approaching businesses that are not yet clients, usually organised around their X-dates.',
        see: ['expiration-date', 'bor', 'x-date'],
    },
    {
        id: 'x-date',
        term: 'X-date',
        category: 'practice',
        essential: true,
        def: 'Shorthand for a prospect\'s expiration date — when their current policy renews and when they can be approached with an alternative.',
        see: ['expiration-date', 'prospecting'],
    },
    {
        id: 'book-of-business',
        term: 'Book of business',
        category: 'practice',
        def: 'All the accounts an agent or agency handles, measured by premium volume and commission.',
        see: ['retention', 'account-rounding'],
    },
    {
        id: 'retention',
        term: 'Retention',
        category: 'practice',
        def: 'The share of accounts that renew with the agency. Also, confusingly, the amount of risk an insured or a carrier keeps — as in self-insured retention.',
        see: ['book-of-business', 'sir', 'account-rounding'],
    },
    {
        id: 'ams',
        term: 'Agency management system',
        short: 'AMS',
        category: 'practice',
        def: 'The system of record for an agency — clients, policies, activities, documents, accounting. AMS360, Applied Epic and HawkSoft are common ones.',
        see: ['download', 'book-of-business'],
    },
    {
        id: 'download',
        term: 'Download',
        category: 'practice',
        def: 'The automated feed of policy data from a carrier into the agency management system, so policies and changes appear without being keyed.',
        detail: 'Download works for admitted, direct-bill personal and small commercial lines. Surplus lines and manuscripted commercial policies usually still get entered by hand, which is where errors enter the system of record.',
        see: ['ams'],
    },
    {
        id: 'csr',
        term: 'Account manager',
        aka: ['csr', 'customer service representative', 'account executive'],
        category: 'practice',
        def: 'The person who services the account day to day — certificates, endorsements, claims, renewals — as distinct from the producer who sells it.',
        see: ['producer', 'retail-agent'],
    },
    {
        id: 'producer',
        term: 'Producer',
        category: 'practice',
        def: 'A licensed salesperson. In an agency it usually means the person who brings the business in and owns the client relationship.',
        see: ['csr', 'producer-license', 'retail-agent'],
    },
    {
        id: 'risk-manager',
        term: 'Risk manager',
        category: 'practice',
        def: 'An employee of a larger business responsible for its insurance and risk programme — the agent\'s counterpart on the client side.',
        see: ['exposure-analysis', 'loss-control'],
    },
    {
        id: 'actuary',
        term: 'Actuary',
        category: 'underwriting',
        def: 'The statistician who prices risk, sets reserves and projects loss development for a carrier.',
        see: ['loss-cost', 'ibnr', 'loss-development'],
    },
    {
        id: 'supply-chain',
        term: 'Supply chain exposure',
        category: 'practice',
        def: 'The risk that a supplier or logistics failure stops the insured\'s business, whether or not the insured suffered any damage themselves.',
        see: ['contingent-business-income', 'business-income'],
    },
    {
        id: 'nfip',
        term: 'National Flood Insurance Program',
        short: 'NFIP',
        category: 'regulatory',
        def: 'The federal flood programme, with commercial limits of $500,000 building and $500,000 contents. Excess flood is bought above it in the private market.',
        see: ['flood', 'dic'],
    },
    {
        id: 'certified-act',
        term: 'Certified act of terrorism',
        category: 'regulatory',
        def: 'An act formally certified by the Treasury Secretary, which is what triggers coverage under the federal terrorism backstop.',
        detail: 'Damage must exceed a statutory threshold before certification is possible, so smaller attacks can go uncertified and uncovered under a TRIA endorsement.',
        see: ['terrorism-coverage', 'tria-rejection'],
    },
    {
        id: 'anti-concurrent-causation',
        term: 'Anti-concurrent causation',
        category: 'property',
        def: 'Property wording excluding a loss if an excluded peril contributed to it at all, even alongside a covered one.',
        detail: 'The reason hurricane claims turn into wind-versus-flood fights: where both contributed and the form has this language, the carrier argues the whole loss is excluded.',
        see: ['proximate-cause', 'flood', 'named-storm'],
    },
    {
        id: 'named-perils',
        term: 'Named perils',
        aka: ['basic form', 'broad form'],
        category: 'property',
        def: 'A property form covering only the causes of loss it lists. If the peril is not named, there is no coverage.',
        confused: 'Special form (all-risk) covers everything except what it excludes — the opposite structure, and much broader.',
        see: ['special-form', 'peril'],
    },
    {
        id: 'special-form',
        term: 'Special form',
        aka: ['all risk', 'open perils'],
        category: 'property',
        essential: true,
        def: 'A property form covering any cause of loss except those it excludes. Broader than named perils, and the standard for most commercial property.',
        detail: 'The burden of proof flips, which matters more than the list: under named perils the insured must show the peril is covered; under special form the carrier must show it is excluded.',
        see: ['named-perils', 'peril', 'exclusion'],
    },
    {
        id: 'vacancy',
        term: 'Vacancy provision',
        category: 'property',
        def: 'Property coverage is cut back or suspended once a building has been vacant beyond a stated period — usually 60 days.',
        detail: 'Vandalism, water damage, theft and glass are typically excluded outright, and other losses paid at 85%. A building between tenants needs a vacancy permit endorsement, which is easy to get and easy to forget.',
        see: ['commercial-property', 'protective-safeguards'],
    },
    {
        id: 'protective-safeguards',
        term: 'Protective safeguards',
        category: 'property',
        def: 'An endorsement making coverage conditional on stated protections being maintained and working — a sprinkler system, a central station alarm, a hood suppression system.',
        detail: 'It is a condition, not a discount: an impaired sprinkler that was not reported can void the fire claim it was meant to prevent.',
        see: ['vacancy', 'cope', 'protection-class'],
    },
    {
        id: 'newly-acquired-property',
        term: 'Newly acquired property',
        category: 'property',
        def: 'An automatic extension covering property bought during the term, for a limited amount and a limited number of days until it is reported.',
        detail: 'Typically 30 days and a capped limit. It is a grace period, not coverage — a location bought in month one and never reported is uninsured by month three.',
        see: ['scheduled-coverage', 'coverage-extension'],
    },
];
